/**
 * @file layout.tsx
 *
 * @fileoverview Application layout context.
 *
 */

import {
  Context,
  createContext,
  Dispatch,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { PathContext } from '~/jobiqo/context/app';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { useLocalStorage } from '~/jobiqo/hooks/useLocalStorage';
import {
  ANONYMOUS_USER,
  isRecruiter,
  ROLE_APPLICANT,
  ROLE_RECRUITER,
} from '~/jobiqo/lib/auth';
import { useIsMobile } from '~/vcl/hooks/useIsMobile';
import { useIsTablet } from '~/vcl/hooks/useIsTablet';

/**
 * Returns a boolean for the given value.
 *
 * @param  {string | boolean} value
 *  The value to be parsed.
 * @returns {boolean}
 *  Boolean of a given value.
 */
const getBool = (value: string | boolean): boolean =>
  !!JSON.parse(String(value).toLowerCase());

export const initialState = {
  setMobileSearchFiltersOpened: (value: boolean) => {},
  mobileSearchFiltersOpened: false,
  isMobile: false,
  toggleSidenavCollapsed: () => {},
  getSidenavCollapsed: () => true,
  setOpenedBottomSubMenu: (title?: string) => {},
};

export interface LayoutContextInterface {
  /**
   * Function to set mobile search filters navigation
   * opened state.
   */
  setMobileSearchFiltersOpened: Dispatch<boolean>;
  /**
   * Mobile search filters navigation opened state.
   */
  mobileSearchFiltersOpened: boolean;
  /**
   * Flag indicating mobile device.
   */
  isMobile: boolean;
  /**
   * The currently opened bottom navigation sub menu.
   */
  openedBottomSubMenu?: string;
  /**
   * Flag indicating if bottom menu is displayed.
   */
  bottomMenuDisplayed?: boolean;
  /**
   * Function to get sidenav collapsed flag.
   */
  getSidenavCollapsed: () => boolean;
  /**
   * Function to toggle sidenav collapsed state.
   */
  toggleSidenavCollapsed: () => void;
  /**
   * Function to set the currently opened bottom navigation sub menu.
   */
  setOpenedBottomSubMenu: (title?: string) => void;
}

/**
 * Application layout context.
 */
export const LayoutContext: Context<LayoutContextInterface> = createContext(
  initialState,
);

export const LayoutProvider = ({ children }) => {
  const { user }: UserContextInterface = useContext(UserContext);
  const { path } = useContext(PathContext);

  const [openedBottomSubMenu, setOpenedBottomSubMenu] = useState<
    string | undefined
  >();
  const [mobileSearchFiltersOpened, setMobileSearchFiltersOpened] = useState<
    boolean
  >(false);
  const [isSidenavCollapsed, setIsSidenavCollapsed] = useLocalStorage(
    `${
      user
        ? isRecruiter(user)
          ? ROLE_RECRUITER
          : ROLE_APPLICANT
        : ANONYMOUS_USER
    }-nav-collapsed`,
    true,
  );
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [bottomMenuDisplayed, setBottomMenuDisplayed] = useState(false);

  const toggleSidenavCollapsed = useCallback(() => {
    setIsSidenavCollapsed(!getBool(isSidenavCollapsed));
  }, [isSidenavCollapsed]);

  const getSidenavCollapsed = useCallback(() => {
    // If the user is not logged in, we will return true
    // (side bar is collapsed as it doesn't exist).
    if (!user?.id) {
      return true;
    }

    return getBool(isSidenavCollapsed);
  }, [user, isSidenavCollapsed]);

  useEffect(() => {
    // Close the sidenav when we reach tablet breakpoint.
    if (!getSidenavCollapsed() && isTablet) {
      toggleSidenavCollapsed();
    }
  }, [isTablet]);

  useEffect(() => {
    openedBottomSubMenu && setOpenedBottomSubMenu(undefined);
  }, [path]);

  useEffect(() => {
    /* Checking if the user is on a mobile device and if the user is logged in. If
    both are true, then we will display the bottom menu. */
    if (isMobile && !!user?.id) {
      setBottomMenuDisplayed(true);
    }
  }, [user?.id, isMobile]);

  return (
    <LayoutContext.Provider
      value={{
        setMobileSearchFiltersOpened,
        mobileSearchFiltersOpened,
        isMobile,
        toggleSidenavCollapsed,
        getSidenavCollapsed,
        openedBottomSubMenu,
        setOpenedBottomSubMenu,
        bottomMenuDisplayed,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};
